import { makeAsyncActionCreator } from 'services/store/utils-actions';
import { collection as todosCollection } from 'services/store/todos/definitions';
import updateRemoteDocuments from 'services/store/utils/actions/updateRemoteDocuments';
import { assertValidScheduleBlocks } from 'utils/schedules/assertValidScheduleBlocks';
import { collection, statusTypes, types } from '../definitions';
import { toSortedScheduleViewBlocks } from './updateScheduleView';

/**
 * Persists the result of a drag-and-drop reschedule as ONE atomic Firestore batch:
 * the schedule's `views.schedule.blocks` plus, for every displaced intent, the
 * `intents.{scheduleId}.start` on its todo document.
 *
 * Why a batch instead of the sequential `updateScheduleView` + per-intent
 * `scheduleIntent` writes the drag used to issue:
 *
 *  - Atomicity: either the whole drop persists or none of it does. The sequential
 *    writes could fail midway, leaving the schedule view and the intent starts
 *    permanently disagreeing.
 *  - No intermediate states: each sequential write produced its own snapshot echo,
 *    re-rendering the schedule from partially-updated data while later writes were
 *    still in flight — the window where overlapping drags read pending/stale state.
 *  - Speed: one commit instead of N+1 read-then-write round trips, shrinking the
 *    window during which a new drag races the previous drop's persistence.
 *
 * The resolved payload is NOT merged into the store (`skip`): realtime data is
 * always on, so the latency-compensated Firestore snapshots deliver both the
 * optimistic and the confirmed state. Merging the ack here could clobber a newer
 * snapshot when acks and snapshots interleave across rapid successive drops.
 */
const persistScheduleDragImpl = makeAsyncActionCreator({
  type: types.UPDATE_SCHEDULE,
  creator: async (schedule, viewBlocks, changedIntents) => {
    const blocks = toSortedScheduleViewBlocks(schedule, viewBlocks);

    // A drag rearranges blocks but never adds or removes them: the write must carry
    // exactly the todos currently stored (identity preservation), with unique starts.
    // Refusing here (throw before any write) beats persisting corruption — a duplicate
    // start or a dropped block historically deleted an intent permanently. (A schedule
    // that has no stored view yet gets its first one from this write; only uniqueness
    // is checked then.)
    const storedBlocks = schedule.views?.schedule?.blocks;

    assertValidScheduleBlocks(blocks, 'persistScheduleDrag', storedBlocks ? {
      expectedTodoIds: storedBlocks.map(block => `${block.todo}`),
    } : {});

    const sent = await updateRemoteDocuments([
      {
        collection,
        id: schedule.id,
        values: {
          'views.schedule.blocks': blocks,
        },
      },
      ...changedIntents.map(intent => ({
        collection: todosCollection,
        id: intent.todo.id,
        values: {
          [`intents.${intent.scheduleId}.start`]: intent.start,
        },
      })),
    ]);

    return sent[0];
  },
  skip: () => true,
  status: {
    path: collection,
    type: statusTypes.updateScheduleView,
    parameters: (schedule) => [ schedule.id ],
  }
});

const persistScheduleDrag = (schedule, viewBlocks, changedIntents) => async (dispatch) => (
  await dispatch(persistScheduleDragImpl(schedule, viewBlocks, changedIntents))
);

export default persistScheduleDrag;
